/**
 * Queries and mutations for assets: reusable, versioned files (logos, terms
 * and conditions) that rules attach and Typst documents embed.
 */
import { queryOptions, useMutation } from '@tanstack/react-query'
import { api, unwrap } from '@/api/client'
import { fetchAll } from '@/api/fetch-all'
import { useInfiniteList } from '@/api/pagination'
import { MAX_PAGE_SIZE } from '@/api/types'
import type { CreateAsset, UpdateAsset } from '@/features/attachments/types'

export interface AssetListFilters {
  /** Application ID, 'shared' for the shared library, or undefined for all. */
  app?: string
  includeShared?: boolean
  q?: string
}

export const assetKeys = {
  all: ['assets'] as const,
  list: (f: AssetListFilters) => ['assets', 'list', f] as const,
  options: (app: string | null) => ['assets', 'options', app ?? 'shared'] as const,
  detail: (id: string) => ['assets', 'detail', id] as const,
}

export function useAssetsList(f: AssetListFilters) {
  return useInfiniteList({
    queryKey: assetKeys.list(f),
    fetchPage: (cursor) =>
      unwrap(
        api.GET('/admin/v1/assets', {
          params: {
            query: {
              app: f.app,
              include_shared: f.app && f.app !== 'shared' ? f.includeShared : undefined,
              q: f.q || undefined,
              limit: 100,
              cursor,
            },
          },
        }),
      ),
  })
}

/** Every asset an application's rules and documents can use: its own plus shared. */
export const assetOptionsQuery = (app: string | null) =>
  queryOptions({
    queryKey: assetKeys.options(app),
    queryFn: () =>
      fetchAll((cursor) =>
        unwrap(
          api.GET('/admin/v1/assets', {
            params: {
              query: {
                app: app ?? 'shared',
                include_shared: app ? true : undefined,
                limit: MAX_PAGE_SIZE,
                cursor,
              },
            },
          }),
        ),
      ),
    staleTime: 60_000,
  })

export const assetQuery = (id: string) =>
  queryOptions({
    queryKey: assetKeys.detail(id),
    queryFn: () => unwrap(api.GET('/admin/v1/assets/{id}', { params: { path: { id } } })),
  })

export function useCreateAsset() {
  return useMutation({
    mutationFn: (body: CreateAsset) => unwrap(api.POST('/admin/v1/assets', { body })),
    meta: { invalidates: [assetKeys.all], successMessage: 'Asset created', errorToast: false },
  })
}

export function useUpdateAsset(id: string) {
  return useMutation({
    mutationFn: (body: UpdateAsset) =>
      unwrap(api.PATCH('/admin/v1/assets/{id}', { params: { path: { id } }, body })),
    meta: { invalidates: [assetKeys.all], successMessage: 'Asset saved', errorToast: false },
  })
}

export function useDeleteAsset() {
  return useMutation({
    mutationFn: (id: string) =>
      unwrap(api.DELETE('/admin/v1/assets/{id}', { params: { path: { id } } })),
    meta: {
      invalidates: [
        [...assetKeys.all, 'list'],
        [...assetKeys.all, 'options'],
      ],
    },
  })
}

export interface UploadInput {
  file: Blob
  filename: string
  notes?: string
}

/** Upload a new version (inactive until activated). The body is the raw file. */
export function uploadAssetVersion(id: string, { file, filename, notes }: UploadInput) {
  return unwrap(
    api.POST('/admin/v1/assets/{id}/versions', {
      params: { path: { id }, query: { filename, notes: notes || undefined } },
      // The generated type is a byte array; send the file as is.
      body: file as unknown as number[],
      bodySerializer: (b: unknown) => b as BodyInit,
      headers: { 'Content-Type': file.type || 'application/octet-stream' },
    }),
  )
}

export function activateAssetVersion(id: string, versionId: string) {
  return unwrap(
    api.POST('/admin/v1/assets/{id}/versions/{version_id}/activate', {
      params: { path: { id, version_id: versionId } },
    }),
  )
}

export function useUploadAssetVersion(id: string) {
  return useMutation({
    mutationFn: (input: UploadInput) => uploadAssetVersion(id, input),
    meta: { invalidates: [assetKeys.all], errorToast: false },
  })
}

export function useActivateAssetVersion(id: string) {
  return useMutation({
    mutationFn: (versionId: string) => activateAssetVersion(id, versionId),
    meta: { invalidates: [assetKeys.all] },
  })
}

export async function fetchAssetVersionContent(id: string, versionId: string): Promise<Blob> {
  const blob = await unwrap(
    api.GET('/admin/v1/assets/{id}/versions/{version_id}/content', {
      params: { path: { id, version_id: versionId } },
      parseAs: 'blob',
    }),
  )
  return blob as Blob
}
